import DOMPurify from 'dompurify';

/**
 * Safely sanitizes and renders HTML content while preventing XSS attacks
 * @param htmlContent - The HTML string to sanitize
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export const sanitizeHtml = (htmlContent: string): string => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return '';
  }

  // Configure DOMPurify to allow safe HTML tags and attributes
  const config = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ol', 'ul', 'li',
      'blockquote', 'code', 'pre',
      'a', 'img'
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'alt', 'src', 'width', 'height',
      'class', 'style'
    ],
    ALLOW_DATA_ATTR: false,
    FORBID_SCRIPT: true,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
    STRIP_COMMENTS: true
  };

  return DOMPurify.sanitize(htmlContent, config);
};

/**
 * React component props for safely rendering HTML content
 */
export interface SafeHtmlProps {
  html: string;
  className?: string;
  tag?: keyof JSX.IntrinsicElements;
}

/**
 * Check if a string contains HTML tags
 * @param content - The content to check
 * @returns true if the content contains HTML tags
 */
export const containsHtml = (content: string): boolean => {
  if (!content || typeof content !== 'string') {
    return false;
  }
  
  // Simple regex to detect HTML tags
  const htmlRegex = /<\/?[a-z][\s\S]*>/i;
  return htmlRegex.test(content);
};

/**
 * Utility to create safe dangerouslySetInnerHTML props
 * @param htmlContent - The HTML content to sanitize
 * @returns Object with __html property for dangerouslySetInnerHTML
 */
export const createSafeHtml = (htmlContent: string) => {
  return {
    __html: sanitizeHtml(htmlContent)
  };
};

/**
 * Strips HTML tags/entities and returns the plain visible text, so search/filtering
 * isn't broken by rich-text formatting like <strong>/<em> wrapping searched words.
 * @param htmlContent - The HTML content to convert to plain text
 * @returns Plain text with collapsed whitespace
 */
export const stripHtmlTags = (htmlContent: string): string => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return '';
  }

  if (!containsHtml(htmlContent)) {
    return htmlContent;
  }

  const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};